import React, { useEffect, useState } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";
import {
    RenderComponentResolver,
    UniformComposition,
    UniformPlayground
} from "@uniformdev/canvas-react";

import { expectContent } from "@/content/utils/expectContent";
import { useLocation } from "@/utils/location-provider";

import { enhanceComposition } from "../clients/uniform-client/enhancers";
import { parseRoute } from "../wac-routes";

import UNIFORM_DEFINITIONS, {
    preloadCompositionComponents,
    preloadPriorityComponentsInComposition
} from "./uniform-lazy-definitions";
import { UniformMissingComponent } from "./utils/uniform-missing-component";

/**
 * Helper to determine if a composition contains any lazy components that need Suspense
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
function compositionHasLazyComponents(composition: any): boolean {
    if (!composition) return false;

    const checkNode = (node: any): boolean => {
        if (node?.type) {
            const component = UNIFORM_DEFINITIONS[node.type];
            if (component?.hasOwnProperty?.("_payload")) {
                return true; // Found a lazy component
            }
        }

        // Check slots recursively
        if (node?.slots) {
            for (const slotItems of Object.values(node.slots)) {
                if (Array.isArray(slotItems)) {
                    if (slotItems.some(checkNode)) {
                        return true;
                    }
                }
            }
        }

        return false;
    };

    return checkNode(composition);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

declare global {
    interface Window {
        _uniformPreloadedComposition?: RootComponentInstance;
    }
}

export type UniformWrapperProps = {
    composition?: RootComponentInstance;
    isUniformPlayground?: boolean;
};

const resolveRenderer: RenderComponentResolver = ({ type }) => {
    const MissingComponent = () => (
        <UniformMissingComponent componentName={type} />
    );

    const component = UNIFORM_DEFINITIONS[type];
    if (!component) {
        console.warn(`No component found for type: ${type}`);
        return MissingComponent;
    }

    return component;
};

export const UniformWrapperLazy = ({
    composition,
    isUniformPlayground
}: UniformWrapperProps) => {
    const [data, setData] = useState<unknown>();
    const [isFetched, setIsFetched] = useState(false);
    const [isPreloaded, setIsPreloaded] = useState(false);
    const [hasLazyComponents, setHasLazyComponents] = useState(false);

    const isStaticFallback = typeof window !== "undefined" && window.IS_STATIC;

    const location = useLocation();

    useEffect(() => {
        const fetchComposition = async () => {
            // Priority 1: Static fallback (highest priority)
            if (isStaticFallback && !isFetched) {
                try {
                    const { path, locale } = parseRoute(location);
                    const slug = path.startsWith("/")
                        ? path.substring(1)
                        : path;

                    const cdn = process.env["UNIFORM_CDN"];

                    if (!cdn) {
                        throw new Error(
                            "UNIFORM_CDN environment variable not set"
                        );
                    }

                    const cdn_path = `${cdn}release/${locale}/${slug}/composition.json`;
                    const req = await fetch(cdn_path);

                    if (!req.ok) {
                        throw new Error(
                            `HTTP ${req.status}: ${req.statusText}`
                        );
                    }

                    const file = await req.json();
                    const enhancedComp = await enhanceComposition(
                        file.compositionApiResponse.composition
                    );

                    setData(enhancedComp);
                    setHasLazyComponents(
                        compositionHasLazyComponents(enhancedComp)
                    );
                    setIsFetched(true);

                    // Preload priority components immediately, then all components
                    preloadPriorityComponentsInComposition(enhancedComp).then(
                        () => {
                            // After priority components are loaded, preload the rest
                            preloadCompositionComponents(enhancedComp).then(
                                () => {
                                    setIsPreloaded(true);
                                }
                            );
                        }
                    );
                } catch (error) {
                    console.error(
                        "Error fetching composition from CDN:",
                        error
                    );
                    setIsFetched(true); // Still mark as fetched to prevent infinite loops
                }
                return;
            }

            // Priority 2: Preloaded composition from window
            if (
                typeof window !== "undefined" &&
                window._uniformPreloadedComposition &&
                !isStaticFallback &&
                !isFetched
            ) {
                // @ts-ignore
                // eslint-disable-next-line react-hooks/exhaustive-deps
                isUniformPlayground = window.isUniformPlayground;
                const compositionData = window._uniformPreloadedComposition;
                setData(compositionData);
                setIsFetched(true);
                setHasLazyComponents(
                    compositionHasLazyComponents(compositionData)
                );

                // Preload priority components immediately, then all components
                preloadPriorityComponentsInComposition(compositionData).then(
                    () => {
                        // After priority components are loaded, preload the rest
                        preloadCompositionComponents(compositionData).then(
                            () => {
                                setIsPreloaded(true);
                            }
                        );
                    }
                );
                return;
            }

            // Priority 3: Composition passed as prop
            if (composition && !isFetched) {
                setData(composition);
                setHasLazyComponents(compositionHasLazyComponents(composition));
                setIsFetched(true);

                // Preload priority components immediately, then all components
                if (!isPreloaded) {
                    preloadPriorityComponentsInComposition(composition).then(
                        () => {
                            // After priority components are loaded, preload the rest
                            preloadCompositionComponents(composition).then(
                                () => {
                                    setIsPreloaded(true);
                                }
                            );
                        }
                    );
                }
                return;
            }

            // Fallback: No composition available
            if (!isFetched) {
                console.warn("⚠️ No composition data available");
                setIsFetched(true);
            }
        };

        fetchComposition();
    }, [composition, isStaticFallback, isPreloaded]);

    if (isUniformPlayground) {
        return (
            <UniformPlayground
                resolveRenderer={resolveRenderer}
                contextualEditingEnhancer={({ composition }) =>
                    enhanceComposition(composition)
                }
            />
        );
    }

    const renderComposition = () => (
        <UniformComposition
            data={expectContent(data as RootComponentInstance)}
            behaviorTracking="onLoad"
            resolveRenderer={resolveRenderer}
            contextualEditingEnhancer={({ composition }) =>
                enhanceComposition(composition)
            }
        />
    );

    return (
        isFetched && (
            <div>
                {hasLazyComponents ? (
                    <React.Suspense fallback={null}>
                        {renderComposition()}
                    </React.Suspense>
                ) : (
                    renderComposition()
                )}
            </div>
        )
    );
};
